// ── Excel export for task lists ─────────────────────────────
// Self-contained helper used by TaskTable's "Export Excel" button.
// It only reads the records handed to it — nothing here mutates app state.
import * as XLSX from "xlsx";
import { cellText, normalizeToMs } from "./helpers";

const IST_OFFSET_MS = 330 * 60 * 1000; // Asia/Kolkata, matches the UI's date rendering
const EXCEL_EPOCH_DAYS = 25569;        // serial for 1970-01-01
const DATE_FMT = "dd-mmm-yyyy hh:mm AM/PM";

// Excel serial built from IST wall-clock so the sheet reads the same as the
// screen, whatever timezone the browser happens to be in. Returns "" when the
// value is empty/unparseable so the cell stays blank instead of showing 1970.
function toExcelDate(val) {
  const ms = normalizeToMs(val);
  if (!ms) return "";
  return (ms + IST_OFFSET_MS) / 86400000 + EXCEL_EPOCH_DAYS;
}

// Same resolution order the grid view uses for the "Assigned To" column.
function assignedUsersText(f) {
  const raw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
  if (Array.isArray(raw)) {
    const names = raw.map(v => v.en_name || v.name || null).filter(Boolean);
    if (names.length) return names.join(", ");
  }
  return cellText(raw) || "";
}

function dash(v) {
  const s = cellText(v);
  return !s || s === "—" ? "" : s;
}

// Mirrors the grid view: a task finished after its due date is reported as
// "Overdue (Done)" rather than plain "Done".
function statusText(f, isUserTable) {
  const status = cellText(f["Overall Status"]) || "Not Started";
  const isDone = status.toLowerCase().includes("done") || status.includes("✅");
  const effectivelyDone = isUserTable ? !!f["My Completion"] : isDone;
  const completedTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);
  const dueTime = normalizeToMs(f["End Date"]);
  const isLate = effectivelyDone && completedTime && dueTime && completedTime > dueTime + 60000;
  return isLate ? "🔴 Overdue (Done)" : status;
}

// Strip emoji/punctuation out of the page title for a safe file name.
function safeFileName(title) {
  const base = String(title || "Tasks")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "Tasks";
}

function stamp() {
  const d = new Date(Date.now() + IST_OFFSET_MS);
  const p = (n) => String(n).padStart(2, "0");
  return {
    file: `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}_${p(d.getUTCHours())}${p(d.getUTCMinutes())}`,
    label: `${p(d.getUTCDate())}-${p(d.getUTCMonth() + 1)}-${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} IST`,
  };
}

/**
 * Download the given task records as an .xlsx file.
 * @param {Array}  records      the rows currently shown on the page (already filtered)
 * @param {Object} opts
 * @param {string} opts.title   page title, used for the sheet heading + file name
 * @param {boolean} opts.isUserTable  true for the employee "My Tasks" tables
 */
export function exportTasksToExcel(records, { title = "Tasks", isUserTable = false } = {}) {
  const rows = Array.isArray(records) ? records : [];

  const COLS = [
    { header: "Sr. No.",             width: 8,  get: (f, i) => i + 1 },
    { header: "Task ID",             width: 14, get: (f) => dash(f["Task ID"]) },
    { header: "Task Name",           width: 34, get: (f) => dash(f["Task Name"]) || "Untitled" },
    { header: "Task Description",    width: 46, get: (f) => dash(f["Task Description"]) },
    { header: "Status",              width: 20, get: (f) => statusText(f, isUserTable) },
    { header: "Priority",            width: 11, get: (f) => dash(f["Priority"]) || "Medium" },
    { header: "Assigned To",         width: 30, get: (f) => assignedUsersText(f) },
    { header: "Assigned By",         width: 22, get: (f) => dash(f["Task Creator"]) || dash(f["Assigned By"]) },
    { header: "Supervisor",          width: 22, get: (f) => dash(f["Supervisor"]) },
    { header: "Company",             width: 22, get: (f) => dash(f["Company Name"]) },
    { header: "Assignee Department", width: 22, get: (f) => dash(f["Assignee Department"]) },
    { header: "Creator Department",  width: 22, get: (f) => dash(f["Creator Department"]) },
    { header: "Start Date",          width: 22, date: true, get: (f) => toExcelDate(f["Start Date"]) },
    { header: "Due Date",            width: 22, date: true, get: (f) => toExcelDate(f["End Date"]) },
    { header: "Completed At",        width: 22, date: true, get: (f) => toExcelDate(f["Completed At"] || f["Completion Time"]) },
    { header: "Completed By",        width: 24, get: (f) => dash(f["Task Completed By"]) || dash(f["Completed By"]) },
    { header: "Recurring",           width: 16, get: (f) => {
        const rc = dash(f["Recurring Type"]);
        return rc === "Not Applicable" ? "" : rc;
      } },
  ];

  if (isUserTable) {
    COLS.splice(5, 0, { header: "My Completion", width: 15, get: (f) => (f["My Completion"] ? "Completed" : "Pending") });
  }

  const when = stamp();
  const heading = `${String(title).trim() || "Tasks"}  |  ${rows.length} task${rows.length === 1 ? "" : "s"}  |  Exported ${when.label}`;

  const aoa = [
    [heading],
    COLS.map(c => c.header),
    ...rows.map((r, i) => COLS.map(c => c.get(r.fields || {}, i))),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Number format for the date columns (data starts on row index 2).
  COLS.forEach((c, ci) => {
    if (!c.date) return;
    for (let ri = 2; ri < aoa.length; ri++) {
      const cell = ws[XLSX.utils.encode_cell({ r: ri, c: ci })];
      if (cell && cell.t === "n") cell.z = DATE_FMT;
    }
  });

  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLS.length - 1 } }];
  ws["!cols"] = COLS.map(c => ({ wch: c.width }));
  ws["!rows"] = [{ hpt: 26 }, { hpt: 20 }];
  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range(
      { s: { r: 1, c: 0 }, e: { r: Math.max(1, aoa.length - 1), c: COLS.length - 1 } }
    ),
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Tasks");
  XLSX.writeFile(wb, `${safeFileName(title)}_${when.file}.xlsx`);
}
